import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField } from '@mui/material';

const PlayerScoreEntry = ({
    playerId,
    playerName,
    playerScore,
    playerState,
    setPlayerState,
    eventId,
    roundKey,
    invalidEntries,
    setInvalidEntries,
    par,
}) => {
    // Local state to track input value
    const [inputValue, setInputValue] = useState(playerScore || '');
    const [isInputValid, setIsInputValid] = useState(true);
    //console.log("Is Input Valid", isInputValid, "Invalid Entries", invalidEntries);
    // Update local state when props change
    useEffect(() => {
        setInputValue(playerScore || '');
    }, [playerScore]);
  
    const scoreDisplay = !inputValue 
        ? 'TBD'
        : isNaN(inputValue)
        ? inputValue.toUpperCase()
        : (Number(inputValue) - par) > 0
        ? `+${Number(inputValue) - par}`
        : (Number(inputValue) - par) < 0
        ? `${Number(inputValue) - par}`
        : 'E';

    
    // Handle score input change
    const handleScoreChange = (e) => {
        const newScore = e.target.value;
        setInputValue(newScore); // Update local state immediately for UI responsiveness
        
        // Check if score is valid
        // Valid Options: Positive number, or 'WD', 'MC', 'DQ', 'NA' (case insensitive)
        const validScore = /^[0-9]+$|^(WD|MC|DQ|NA)$/i.test(newScore);
        
        if (!validScore && newScore !== '') {
            //console.warn("Invalid score input:", newScore);
            setIsInputValid(false);
            
            // Check if the player ID is already in the invalidEntries array
            if (!invalidEntries.some(entry => entry.playerId === playerId)) {
                setInvalidEntries((prev) => [...prev, { playerId }]);
            }
            
            return; // Exit if the score is invalid
        } else if (validScore && !isInputValid) {
            // If the score is valid and was previously invalid, reset the state
            setIsInputValid(true);
            setInvalidEntries((prev) => prev.filter(entry => entry.playerId !== playerId));
        }
        
        // Find player in state and update their score
        if (playerState && setPlayerState) {
            const newPlayerState = [...playerState];
            const playerIndex = newPlayerState.findIndex(player => player.playerId === playerId);
            
            if (playerIndex !== -1) {
                // Make a deep copy of the player object
                const updatedPlayer = JSON.parse(JSON.stringify(newPlayerState[playerIndex]));
                
                // Initialize the scores object if it doesn't exist
                if (!updatedPlayer.scores) {
                    updatedPlayer.scores = {};
                }
                
                // Initialize the event object if it doesn't exist
                if (!updatedPlayer.scores[eventId]) {
                    updatedPlayer.scores[eventId] = {};
                }
                
                if (newScore === '') {
                    // If the field is empty, remove the property completely instead of setting to empty string
                    if (updatedPlayer.scores[eventId][roundKey] !== undefined) {
                        delete updatedPlayer.scores[eventId][roundKey];
                    }
                } else {
                    // Process the score value - convert to number if numeric, keep as string if text
                    let processedScore = newScore;
                    if (/^[0-9]+$/.test(newScore)) {
                        // If it's a numeric string, convert to number
                        processedScore = Number(newScore);
                    } else {
                        // If it's text, convert to uppercase
                        processedScore = newScore.toUpperCase();
                    }
                    
                    // Set the round score with the processed value
                    updatedPlayer.scores[eventId][roundKey] = processedScore;
                }
                
                // Update the state with the modified player
                newPlayerState[playerIndex] = updatedPlayer;
                setPlayerState(newPlayerState);
            } 
        } 
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '90%',
                maxWidth: '600px',
                margin: '0 auto',
                borderBottom: '1px solid #eaeaea',
            }}
        >
            {/* Player information section */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start', // Ensures text is left-aligned
                    textAlign: 'left', // Explicitly set text alignment
                    flex: 1, // Take available space
                }}
            >
                {/* Player name */}
                <Typography 
                    variant="h6"
                    sx={{ 
                        fontWeight: 700, 
                        fontSize: '18px',
                        lineHeight: 1.2,
                        textAlign: 'left', // Explicitly set text alignment
                        width: '100%', // Ensure the typography takes full width
                    }}
                >
                    {playerName}
                </Typography>
                
                {/* Player score status */}
                <Typography 
                    variant="body1"
                    sx={{ 
                        color: (inputValue && isInputValid) ? '#00AA72' : '#CC293C',
                        fontSize: '16px',
                        lineHeight: 1.2,
                        textAlign: 'left', // Explicitly set text alignment
                        width: '100%', // Ensure the typography takes full width
                    }}
                >
                    Score: {scoreDisplay}
                </Typography>
            </Box>
            
            {/* Score input field */}
            <Box sx={{ 
                width: '100px', 
                height: '80px', 
                display: 'flex', 
                alignItems: 'center',
                marginLeft: '16px', // Add some space between text and input
            }}>
                <TextField
                    variant="outlined"
                    value={inputValue}
                    onChange={handleScoreChange}
                    placeholder="Score"
                    type="text"
                    inputProps={{ 
                        style: { 
                            fontSize: '18px', 
                            padding: '10px',
                            textAlign: 'center'
                        }
                    }}
                    sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                            borderColor: isInputValid ? '#002129' : '#CC293C',
                            '& fieldset': {
                                borderWidth: '2px',
                                borderColor: isInputValid ? '#002129' : '#CC293C',
                            },
                            '&:hover fieldset': {
                                borderColor: isInputValid ? '#002129' : '#CC293C',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: isInputValid ? '#002129' : '#CC293C',
                            },
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

export default PlayerScoreEntry;